import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { userVouchersApi } from '../api';
import type {
  BanUserVoucherPayload,
  UserVoucherItem,
  UserVoucherSummary,
} from '../types';
import { getDefaultSummary, getErrorMessage } from '../utils';

const DEFAULT_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 400;

export function useUserVouchers() {
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  const [summary, setSummary] = useState<UserVoucherSummary>(getDefaultSummary());
  const [items, setItems] = useState<UserVoucherItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPageState] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [search, setSearchState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const setPage = useCallback((nextPage: number) => {
    setPageState(nextPage);
  }, []);

  const setSearch = useCallback((value: string) => {
    setSearchState(value);
    setPageState(1);
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [search]);

  const fetchList = useCallback(
    async (nextPage: number, keyword: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);

      try {
        const result = await userVouchersApi.list(
          { page: nextPage, limit, search: keyword || undefined },
          controller.signal
        );
        if (!mountedRef.current) return;
        setSummary(result.summary);
        setTotalCount(result.totalCount);
        setItems(result.items);
      } catch (err) {
        if (mountedRef.current) {
          setError(new Error(getErrorMessage(err)));
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [limit]
  );

  const refetch = useCallback(
    () => fetchList(page, debouncedSearch),
    [fetchList, page, debouncedSearch]
  );

  const ban = useCallback(async (payload: BanUserVoucherPayload) => {
    setUpdating(true);
    try {
      return await userVouchersApi.ban(payload);
    } finally {
      if (mountedRef.current) setUpdating(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    fetchList(page, debouncedSearch);
  }, [fetchList, page, debouncedSearch]);

  const pageCount = Math.max(1, Math.ceil(totalCount / limit));

  return useMemo(
    () => ({
      summary,
      items,
      totalCount,
      page,
      pageCount,
      limit,
      search,
      loading,
      updating,
      error,
      setPage,
      setSearch,
      refetch,
      ban,
    }),
    [
      ban,
      error,
      items,
      limit,
      loading,
      page,
      pageCount,
      refetch,
      search,
      setPage,
      setSearch,
      summary,
      totalCount,
      updating,
    ]
  );
}
